import { MOCK_LIVE_STREAMS } from '@/features/marketing/mockData/streamEvents'

/**
 * Single featured stream view — large player area + chat sidebar
 * + stream meta. Used as the lead visual on /explore/live to sell
 * the "embedded player, no app switching" pitch.
 *
 * Static composition — no real Twitch iframe (would require a real
 * stream ID and CSP whitelist). Just a thumbnail with a fake play
 * button overlay; visitors recognise the format.
 */
export function StreamHeroMockup() {
  const featured = MOCK_LIVE_STREAMS[0]!
  const others = MOCK_LIVE_STREAMS.slice(1)

  return (
    <div className="flex aspect-video w-full overflow-hidden">
      {/* Player column */}
      <div className="relative flex-1 overflow-hidden bg-black">
        <img
          src={featured.thumbnailUrl}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-90"
          loading="lazy"
        />
        {/* Vignette */}
        <div
          className="absolute inset-0"
          style={{
            background:
              'linear-gradient(180deg, rgba(0,0,0,0.0) 50%, rgba(0,0,0,0.65) 100%)',
          }}
        />
        {/* Live pip top-left */}
        <span className="font-display absolute left-3 top-3 inline-flex items-center gap-1.5 rounded-sm bg-danger px-2 py-0.5 text-[10px] font-extrabold tracking-[0.2em] text-white uppercase">
          <span className="size-1.5 rounded-full bg-white" />
          Live
        </span>
        <span className="font-display absolute right-3 top-3 rounded-sm bg-black/70 px-2 py-0.5 text-[10px] font-bold text-white tabular-nums">
          {featured.viewerCount.toLocaleString()} watching
        </span>
        {/* Play button — purely decorative */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div
            aria-hidden
            className="flex size-14 items-center justify-center rounded-full backdrop-blur"
            style={{
              backgroundColor: 'rgba(0,0,0,0.55)',
              border: '2px solid rgba(255,255,255,0.85)',
            }}
          >
            <span
              className="ml-1 block h-0 w-0"
              style={{
                borderLeft: '14px solid white',
                borderTop: '9px solid transparent',
                borderBottom: '9px solid transparent',
              }}
            />
          </div>
        </div>
        {/* Stream meta bottom strip */}
        <div className="absolute bottom-0 left-0 right-0 flex items-start gap-2.5 p-3">
          <img
            src={featured.avatarUrl}
            alt=""
            className="size-9 shrink-0 rounded-full border-2 border-white/80"
            loading="lazy"
          />
          <div className="min-w-0 flex-1">
            <p className="line-clamp-1 text-sm font-bold text-white">
              {featured.streamTitle}
            </p>
            <p className="font-display mt-0.5 text-[10px] tracking-[0.15em] text-white/75 uppercase">
              {featured.streamerCallsign} · {featured.gameName}
            </p>
          </div>
        </div>
      </div>

      {/* Chat sidebar */}
      <div className="hidden w-[28%] flex-col border-l border-border bg-bg/80 md:flex">
        <div className="border-b border-border px-3 py-2">
          <p className="font-display text-[10px] font-extrabold tracking-[0.22em] text-fg-subtle uppercase">
            Squadron chat
          </p>
        </div>
        <div className="flex-1 space-y-1.5 overflow-hidden px-3 py-2 text-[11px]">
          <ChatLine name="@stormbreaker" colour="#D946EF" body="GG that round" />
          <ChatLine name="@vega" colour="#3D6FFF" body="nice pop" />
          <ChatLine name="@halcyon" colour="#00ff88" body="🔥🔥" />
          <ChatLine name="@cypher" colour="#ffb300" body="watch the smoke" />
          <ChatLine name="@orion" colour="#D946EF" body="rotating B now" />
          <ChatLine name="@paladin" colour="#3D6FFF" body="held it!" />
          <ChatLine name="@tempest" colour="#D946EF" body="huge clutch" />
        </div>
        {/* "Now streaming" footer pill list */}
        <div className="border-t border-border px-3 py-2.5">
          <p className="font-display text-[9px] font-extrabold tracking-[0.22em] text-fg-subtle uppercase">
            Also live
          </p>
          <div className="mt-1.5 space-y-1">
            {others.slice(0, 2).map((s) => (
              <div key={s.twitchLogin} className="flex items-center gap-1.5">
                <img
                  src={s.avatarUrl}
                  alt=""
                  className="size-4 rounded-full border border-border"
                  loading="lazy"
                />
                <span className="font-display truncate text-[10px] text-fg-muted">
                  {s.streamerCallsign}
                </span>
                <span className="font-display ml-auto text-[10px] text-fg-subtle tabular-nums">
                  {s.viewerCount}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

function ChatLine({
  name,
  colour,
  body,
}: {
  name: string
  colour: string
  body: string
}) {
  return (
    <p className="leading-tight">
      <span
        className="font-display font-extrabold"
        style={{ color: colour }}
      >
        {name}
      </span>
      <span className="text-fg-muted"> {body}</span>
    </p>
  )
}
